"use client";

import type { JSX } from "react";
import Link from "next/link";
import Image from "next/image";
import { cva, type VariantProps } from "class-variance-authority";
import type { DirectusFile } from "@/lib/directus";
import { getAssetUrl } from "@/lib/directus";

const tileVariants = cva(
  "group relative flex flex-col justify-end overflow-hidden rounded-2xl bg-gray-900 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:shadow-md",
  {
    variants: {
      gradient: {
        indigo: "bg-gradient-to-br from-indigo-500 to-blue-600",
        green: "bg-gradient-to-br from-green-500 to-emerald-600",
        amber: "bg-gradient-to-br from-amber-500 to-orange-600",
      },
    },
    defaultVariants: {
      gradient: "indigo",
    },
  }
);

interface CalculatorTileProps extends VariantProps<typeof tileVariants> {
  title: string;
  description: string;
  href: string;
  image?: number | DirectusFile | null;
  className?: string;
}

export function CalculatorTile({
  title,
  description,
  href,
  gradient,
  image,
  className,
}: CalculatorTileProps): JSX.Element {
  const imageData = typeof image === 'object' && image !== null ? image : null;
  const imageUrl = image ? getAssetUrl(image) : '';
  const hasImage = Boolean(imageUrl && imageData);

  return (
    <Link
      href={href}
      className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-2xl"
    >
      <article className={tileVariants({ gradient, className })}>
        {/* Aspect ratio container to prevent CLS - 16:9 ratio for more compact tiles */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          {/* Background image or gradient */}
          {hasImage ? (
            <Image
              alt={imageData?.description || imageData?.title || title}
              src={imageUrl}
              fill
              className="absolute inset-0 object-cover transition-[filter] duration-300 group-hover:brightness-110 group-active:brightness-110"
              quality={75}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          ) : null}

          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

          {/* Content overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <h3 className="text-xl font-semibold leading-tight text-white sm:text-2xl">
              {title}
            </h3>
            <p className="mt-2 text-xs leading-5 text-white/90 sm:text-sm">
              {description}
            </p>
          </div>
        </div>
      </article>
    </Link>
  );
}
